import { useEffect, useState } from 'react';
import { useAuth } from '../app/Auth';
import { ErrorNote, Icon, PageHeader, SecretField } from '../app/ui';
import { callFn } from '../lib/api';
import { APP_VERSION, STAGE_LABEL, nexusHostLabel } from '../lib/config';
import { useApi } from '../lib/useApi';
import { enrollPasskey, mfaStatus, passkeysSupported, removePasskey } from '../lib/passkey';
import type { MfaStatus } from '../lib/passkey';

/** Same steps and note as AQTokenHelp in SettingsView.swift. */
export const AQ_TOKEN_STEPS = [
  'Open https://aquastar-admin-dev.kstack.app and sign in with a normal password sign-in (a token cannot create a token).',
  'Left nav → Access tokens.',
  'Name it e.g. "Helm (Web Dev)", pick the longest expiry.',
  'Create token — it is shown once, copy it.',
  'Paste it here and press Connect. Helm stores it in the Nexus credential store and never shows it again.',
];
export const AQ_TOKEN_NOTE = 'Note: on AquaStar dev\'s current Nexus version the token carries your platform-admin flag automatically. After AquaStar dev is upgraded past NEXUS-586, a NEW token needs the "Platform admin access" checkbox and a passkey step-up.';
const AQ_TOKENS_URL = 'https://aquastar-admin-dev.kstack.app/platform/access-tokens';

function AquaStarSection() {
  const { toast, me } = useAuth();
  const st = useApi<{ connected: boolean }>('aq_status');
  const [token, setToken] = useState('');
  const [help, setHelp] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const connected = st.data?.connected;

  async function connect() {
    setBusy(true); setMsg(null);
    try { await callFn('aq_connect', { token: token.trim() }); setToken(''); await st.reload(true); toast('AquaStar connected', 'ok'); }
    catch (e) { setMsg(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }
  async function disconnect() {
    if (!window.confirm('Disconnect AquaStar? Helm stops reading and updating their dev issues until you reconnect.')) return;
    setBusy(true); setMsg(null);
    try { await callFn('aq_disconnect'); await st.reload(true); }
    catch (e) { setMsg(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <section className="card form">
      <h2>AquaStar — paying customer</h2>
      <div className="kv"><span>Status</span><span className={connected ? 'good-text' : 'muted'}>{connected == null ? '…' : connected ? 'Connected' : 'Not connected'}</span></div>
      {connected === false ? (
        <>
          <label htmlFor="aq-token">AquaStar dev access token</label>
          <div className="row gap">
            <div className="grow"><SecretField id="aq-token" value={token} onChange={setToken} placeholder="AquaStar dev access token" autoComplete="off" /></div>
            <button type="button" className="icon-btn" onClick={() => setHelp(!help)} aria-expanded={help} aria-label="How to create an AquaStar dev access token"><Icon name="info" /></button>
          </div>
          {help ? (
            <div className="help card inset" role="note">
              <strong>Create an AquaStar dev access token</strong>
              <ol>{AQ_TOKEN_STEPS.map((s) => <li key={s}>{s}</li>)}</ol>
              <p className="muted small">{AQ_TOKEN_NOTE}</p>
              <a className="btn small" href={AQ_TOKENS_URL} target="_blank" rel="noopener noreferrer">Open Access tokens page ↗</a>
            </div>
          ) : null}
          <button className="btn primary" disabled={busy || token.trim().length < 10 || me?.role !== 'owner'} onClick={() => void connect()}>{busy ? 'Checking…' : 'Connect'}</button>
          {me && me.role !== 'owner' ? <p className="muted small">Only the owner connects AquaStar.</p> : null}
        </>
      ) : connected ? <button className="btn bad" disabled={busy} onClick={() => void disconnect()}>Disconnect</button> : null}
      <ErrorNote text={msg ?? st.error} />
      <p className="muted small">Helm reads and updates AquaStar's DEV issues through their tracker's existing API. Create a platform-admin access token on aquastar-admin-dev.kstack.app (Profile → Access tokens) and paste it here; it is checked, then stored in the Nexus credential store and never shown again. Nothing is installed or changed on AquaStar's side.</p>
    </section>
  );
}

function PasskeysSection() {
  const { toast } = useAuth();
  const [st, setSt] = useState<MfaStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const load = () => { mfaStatus().then(setSt).catch((e) => setErr(e instanceof Error ? e.message : String(e))); };
  useEffect(load, []);
  async function run(fn: () => Promise<void>) {
    setBusy(true); setErr(null);
    try { await fn(); load(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  }
  return (
    <section className="card form">
      <h2>Passkeys</h2>
      <p className="muted small">Helm web sign-in needs a passkey. Add a second one (another device or a hardware key) so losing one never locks you out.</p>
      {st?.passkeys.map((p) => (
        <div className="kv" key={p.id}>
          <span>{p.name}{p.backedUp ? ' · synced' : ''}</span>
          <span>{p.lastUsedAt ? `used ${new Date(p.lastUsedAt).toLocaleDateString()}` : 'not used yet'}{' '}
            <button className="btn bad" disabled={busy || st.passkeys.length < 2} title={st.passkeys.length < 2 ? 'Add another passkey before removing the last one' : ''}
              onClick={() => void run(async () => { await removePasskey(p.id); toast('Passkey removed', 'ok'); })}>Remove</button></span>
        </div>
      ))}
      {st ? <div className="kv"><span>Recovery codes left</span><span>{st.recoveryCodesRemaining}</span></div> : null}
      <button className="btn" disabled={busy || !st || !passkeysSupported()}
        onClick={() => void run(async () => { const r = await enrollPasskey(`Passkey ${new Date().toLocaleDateString()}`); toast('Passkey added', 'ok'); if (r.recoveryCodes) setCodes(r.recoveryCodes); })}>Add a passkey</button>
      {codes ? <pre className="mono">{codes.join('\n')}</pre> : null}
      <ErrorNote text={err} />
    </section>
  );
}

export function SettingsPage() {
  const { me, signOut } = useAuth();
  return (
    <section className="page narrow">
      <PageHeader title="Settings" />
      <section className="card form">
        <h2>Nexus</h2>
        <div className="kv"><span>Stage</span><span>{STAGE_LABEL}</span></div>
        <div className="kv"><span>Host</span><span className="mono">{nexusHostLabel()}</span></div>
        <div className="kv"><span>App key</span><span className="muted">stamped by the edge — never in this page</span></div>
        <div className="kv"><span>Session</span><span>signed in (refresh token rotates automatically)</span></div>
      </section>
      <PasskeysSection />
      <AquaStarSection />
      <section className="card form">
        <h2>Account</h2>
        {me ? (
          <>
            <div className="kv"><span>Signed in as</span><span>{me.user.name ?? me.user.id}</span></div>
            <div className="kv"><span>Company</span><span>{me.company.name} ({me.role})</span></div>
          </>
        ) : null}
        <button className="btn bad" onClick={signOut}>Sign out</button>
      </section>
      <section className="card form">
        <h2>About</h2>
        <div className="kv"><span>Version</span><span>{APP_VERSION} · web · {STAGE_LABEL}</span></div>
        <p className="muted small">Helm — the K-Stack business OS. Plan: docs/PLAN_HELM_V1.md (P0b Browser).</p>
      </section>
    </section>
  );
}
